import { Observable, of, map, tap, switchMap, from, concatMap, last } from "rxjs";
import {
  MenuModule,
  Application,
  MenuOption,
  MenuAccessAdapter,
  MenuAdapter,
  MenuAccess,
  MenuId,
  MenuOptionId,
  LockedMenuMessageAdapter,
  LockedMenuMessage,
  UserMenuItem,
  UserMenuItemRequestAdapter,
  UserMenuItemResponseAdapter
} from "@app/shared/models/menu.model";
import { IPLUS_URL } from "@app/shared/constants/url.constants";
import { TabService } from "@app/core/services/tab.service";
import { NotifyService } from "@app/core/services/notify.service";
import { MessageService } from "./message.service";
import { Injectable, inject } from "@angular/core";
import { AuthenticationService } from "@app/core/services/authentication.service";
import { APPLICATIONS } from "@app/shared/constants/applications.constants";
import { UserSettingsService } from "@app/core/services/user-settings.service";
import { MENU_OPTIONS } from "@app/shared/constants/menu-options.constants";
import { UserEventsService } from "@app/core/services/user-events.service";
import { UserEventTypes } from "@app/shared/models/user-events.model";
import { HttpService } from "@app/core/services/http/http.service";

/**
 * Base Menu Service
 */
@Injectable({
  providedIn: "root"
})
export abstract class BaseMenuService {
  tabService = inject(TabService);
  protected http = inject(HttpService);
  protected notifyService = inject(NotifyService);
  protected messageService = inject(MessageService);
  private userEventService = inject(UserEventsService);
  private authService = inject(AuthenticationService);
  private userSettingsService = inject(UserSettingsService);

  private _applications: Application[];
  private _modules: MenuModule[] = [];
  private _menuOptions: MenuOption[] = [];
  private _userMenuOptions: UserMenuItem[] = [];

  /**
   * Load menu data from server by user id
   */
  fetchApplications() {
    const menuAdapter: MenuAdapter = new MenuAdapter();
    this.clearData();
    return this.http.get(IPLUS_URL.MENU_ITEMS_BY_USER).pipe(
      switchMap((response) => {
        return this._flatten(menuAdapter.adapt(response));
      })
    );
  }

  /**
   * Fetch user menu items
   * @returns
   */
  fetchUserMenuItems() {
    const menuAdapter: UserMenuItemResponseAdapter = new UserMenuItemResponseAdapter();
    return this.http
      .addDataParams({ dashboardItems: [] })
      .get(IPLUS_URL.USER_DASHBOARD_MENU_ITEM_LIST)
      .pipe(
        map((response: any) => menuAdapter.adapt(response)),
        tap((menuItems: UserMenuItem[]) => {
          this._setUserMenuItems(menuItems);
        })
      );
  }

  /**
   * Refresh menu items only when a user is authenticated and if menu options exist already
   */
  refreshMenus() {
    if (this.authService.isAuthenticated() && this._menuOptions?.length) {
      this.fetchApplications()
        .pipe(
          switchMap(() => {
            return this.fetchUserMenuItems();
          })
        )
        .subscribe();
    }
  }

  /**
   * Update order of dashboard items for user.
   * @returns
   */
  updateUserMenuItems() {
    const reqAdapter: UserMenuItemRequestAdapter = new UserMenuItemRequestAdapter();
    const respAdapter: UserMenuItemResponseAdapter = new UserMenuItemResponseAdapter();
    return this.http
      .skipLoaderHandler()
      .addDataParams(reqAdapter.adapt(this._userMenuOptions))
      .put(IPLUS_URL.USER_DASHBOARD_MENU_ITEM_LIST, "")
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  /**
   * Retrieve locked message based on language for the menu item
   * @param id
   * @returns
   */
  fetchLockedMessage(id: string) {
    const menuAccessAdapter: LockedMenuMessageAdapter = new LockedMenuMessageAdapter();
    return this.http
      .addDataParams({ id: id })
      .get<LockedMenuMessage>(IPLUS_URL.MENU_ITEMS_LOCKED_MESSAGE)
      .pipe(map((response: any) => menuAccessAdapter.adapt(response)));
  }

  /**
   * Get array of all system aaplications
   */
  fetchAllApplications(): Observable<Application[]> {
    const menuAdapter: MenuAdapter = new MenuAdapter();
    return this.http.get(IPLUS_URL.MENU_ITEMS_BY_APPLICATION).pipe(map((response: any) => menuAdapter.adapt(response)));
  }

  /**
   * Determine what menu options are shown to the user
   * @param data
   */
  canShowMenuOption(data: MenuOptionId[]): boolean {
    if (data.length === 0) {
      return false;
    }

    // Show all menu options if system admin and no admin options are found.
    if (this.authService.isSystemAdmin && (!this.hasMenuOptions() || !this._menuOptions.find((x) => x.application === APPLICATIONS.ADMIN))) {
      return true;
    }

    for (const d of data) {
      const menu: MenuOption = this.menuOptions.find((x) => x.id === d.id);
      if (menu) {
        return true;
      }
    }
    return false;
  }

  /**
   * Clear menu data arrays
   */
  clearData() {
    this._applications = [];
    this._menuOptions = [];
    this._modules = [];
    this._userMenuOptions = [];
  }

  /**
   * Check if user has any menu options
   */
  hasMenuOptions = (): boolean => {
    return this._menuOptions?.length > 0;
  };

  /**
   * Get application data
   *
   * @param id
   */
  getApplication(id: string): Application {
    return this._applications.find((app) => app.id === id);
  }

  /**
   * Get module data for a specific module id
   *
   * @param id
   */
  getMenuModule(id: string): MenuModule {
    return this._modules.find((mod) => mod.id === id);
  }

  /**
   * Get menu option for specific menu id
   * @param id
   */
  getMenuOption(id: string): MenuOption {
    return this._menuOptions.find((menu) => menu.id === id);
  }

  /**
   * Update new sequence and order of array based on user-dashboard.
   * @param menuItems
   */
  updateUserMenuItemIndex() {
    this._userMenuOptions.forEach((x, i) => {
      x.menuSequence = i;
    });
  }

  /**
   * Get array of available application
   */
  get applications(): Application[] {
    return this._applications;
  }

  /**
   * Get array of available modules
   */
  get modules(): MenuModule[] {
    return this._modules;
  }

  /**
   * Get array of available menu options
   */
  get menuOptions(): MenuOption[] {
    return this._menuOptions;
  }

  get userMenuItems(): UserMenuItem[] {
    return this._userMenuOptions;
  }

  /**
   * Determine if user can acccess a menu id
   *
   * It's possible that user's menu security has changed after they signed on.
   *
   */
  protected canAccessMenuId(data: MenuAccess): Observable<MenuAccess> {
    const menuAccessAdapter: MenuAccessAdapter = new MenuAccessAdapter();
    return this.http
      .addDataParams(data)
      .get(IPLUS_URL.MENU_ITEMS_VALIDATE_MENU)
      .pipe(map((response: any) => menuAccessAdapter.adapt(response)));
  }

  /**
   * Sets the amount of modules and menu options that are available per user
   * @param applications
   */
  private _flatten(applications: Application[]) {
    if (!applications?.length) {
      return of(null);
    }

    // flatten menu applications
    this._applications = applications;

    return from(this._applications).pipe(
      concatMap((application) => {
        // Flatten modules
        return application.modules.map((module) => {
          return { application: application.id, ...module } as MenuModule;
        });
      }),
      tap((module) => {
        this._modules.push(module);
      }),
      last(),
      switchMap(() => {
        // Flatten menu options
        return from(this._modules).pipe(
          concatMap((module) => {
            return from(module.groups).pipe(
              tap((group) => {
                this._menuOptions.push(...group.items);
              })
            );
          }),
          last(),
          tap(() => {
            // Filter out ADMIN applications (if any). This needs to be a post process in order to properly build menu option array.
            this._applications = applications.filter((x) => x.id !== APPLICATIONS.ADMIN);
          })
        );
      })
    );
  }

  /**
   * Determine if conditional menu items should be shown
   * @param id
   * @returns
   */
  private _canAddUserMenuItem(menuItem: string) {
    if (!this.userSettingsService.userSettings) {
      return false;
    }

    switch (menuItem) {
      case MENU_OPTIONS.USER.ONDEMAND_REPORTS.id: {
        return this.userSettingsService?.ondemandSettings?.canOnDemandExecute;
      }
      case MENU_OPTIONS.USER.INBASKET.id: {
        return this.userSettingsService?.inbasketSettings?.hasInbasket;
      }
      case MENU_OPTIONS.USER.ATTACH_DOCUMENTS.id: {
        return this.userSettingsService?.inbasketSettings?.hasInbasket;
      }
      case MENU_OPTIONS.USER.DOCUMENTS_PLUS.id: {
        return false;
      }
      case MENU_OPTIONS.USER.DOCUMENT_WIZARD_LIST.id: {
        return false;
      }
      case MENU_OPTIONS.USER.HINTS_TIPS.id: {
        return this.userSettingsService.isTipsAndHintsEnabled;
      }
      default: {
        return true;
      }
    }
  }

  /**
   * Setup user menu items for dashboard items then build applications in correct order from server.
   * If no list is present a default one will be created.
   * @param menuItems
   */
  private _setUserMenuItems(menuItems?: UserMenuItem[]) {
    if (menuItems?.length) {
      // Add back all applications in case they were removed during re-ordering when disabled.
      const items = [
        ...menuItems,
        ...this.applications.map((app) => ({
          menuItem: app.id,
          menuSequence: 0
        }))
      ];

      items.forEach((x, i) => {
        const item = {
          menuItem: x.menuItem,
          menuSequence: i,
          application: this.applications.find((a) => a.id === x.menuItem)
        };

        // Check for duplicates
        if (this._userMenuOptions.find((um) => um.menuItem === item.menuItem)) {
          return;
        }

        // Check if menu item exists
        if (Object.keys(MENU_OPTIONS.USER).findIndex((key) => MENU_OPTIONS.USER[key].id === item.menuItem) === -1 && !item.application) {
          return;
        }

        // Do not add applications that the user no longer has access too
        if ([APPLICATIONS.COMMON, APPLICATIONS.GROUP, APPLICATIONS.INDIVIDUAL, APPLICATIONS.WC].includes(item.menuItem) && !item.application) {
          return;
        }

        this._userMenuOptions.push(item);
      });

      this._checkConditionalMenuItem(MENU_OPTIONS.USER.INBASKET.id, 2);
      this._checkConditionalMenuItem(MENU_OPTIONS.USER.DOCUMENTS_PLUS.id, 3);
      this._checkConditionalMenuItem(MENU_OPTIONS.USER.ATTACH_DOCUMENTS.id, 4);
      this._checkConditionalMenuItem(MENU_OPTIONS.USER.ONDEMAND_REPORTS.id, 5);
      this._checkConditionalMenuItem(MENU_OPTIONS.USER.DOCUMENT_WIZARD_LIST.id, 6);
      this._checkConditionalMenuItem(MENU_OPTIONS.USER.HINTS_TIPS.id, 99);

      this._userMenuOptions.sort((a, b) => a["menuSequence"] - b["menuSequence"]);
    } else {
      this._userMenuOptions = [
        ...Object.keys(MENU_OPTIONS.USER)
          .map((key) => {
            const menuItem: string = MENU_OPTIONS.USER[key].id;

            if (!this._canAddUserMenuItem(menuItem)) {
              return null;
            }

            return { menuItem: menuItem };
          })
          .filter(Boolean),
        ...this.applications.map((app) => ({
          menuItem: app.id,
          application: app
        }))
      ];
    }
    this.messageService.sendMenuOptionRefreshMessage();
  }

  /**
   * Check specific menu items that are shown conditionally. Remove them from the list if they can no longer be shown and exist in the list
   * or inject them into the list if they need to be shown and are not in the list.
   * @param menuItem
   * @param insertIndex
   */
  private _checkConditionalMenuItem(menuItem: string, insertIndex: number = 0) {
    const canAdd: boolean = this._canAddUserMenuItem(menuItem);
    const existsIndex: number = this._userMenuOptions.findIndex((x) => x.menuItem === menuItem);
    const exists: boolean = existsIndex > -1;

    if (canAdd && !exists) {
      this._userMenuOptions.splice(insertIndex, 0, {
        menuItem: menuItem,
        menuSequence: insertIndex
      });
    } else if (!canAdd && exists) {
      this._userMenuOptions.splice(existsIndex, 1);
    }
  }

  //
  // - Webfacing Routines
  //

  /**
   * Get webfacing invocation url for legacy application
   *
   * @param appId
   */
  protected getWebFaceUrl(appId: string) {
    return this._applications.find((app) => app.id === appId)?.url;
  }

  /**
   * Determine if legacy application is open
   *
   * Only one webface invocation can be opened at a time
   *

   */
  protected isLegacyAppOpen(): boolean {
    const isOpen = this.tabService.isApplicationOpen();

    if (isOpen) {
      const message = "APP.MSG_ONLY_ONE_MENU";
      const title = "APP.MSG_WARNING";
      this.notifyService.warning(title, message);
      return true;
    }

    return false;
  }

  // --
  // Favorites Menu Routines
  // --

  /**
   * Toggle favorite status for a menu optin
   * @param id
   */
  toggleFavorite(id: string) {
    const menu = this.menuOptions.find((option) => option.id === id);
    if (menu.security.canFavorite) {
      if (menu.security.isFavorite) {
        this.userEventService.removeEvent(UserEventTypes.Favorites, menu.id);
        return this._deleteFavorite(menu).pipe(tap(() => this._updateFavoritesTab(menu)));
      } else {
        this.userEventService.addEvent(UserEventTypes.Favorites, menu.itemDescription.shortDescription, menu.id, menu.application, menu.isLegacy);
        return this._addFavorite(menu).pipe(tap(() => this._updateFavoritesTab(menu)));
      }
    }

    return of(undefined);
  }

  /**
   * Check if the menu option is favorited
   */
  isFavorite(id: string): boolean {
    if (this.favoritesMenuOptions.find((option) => option.id === id)) {
      return true;
    }
    return false;
  }

  /**
   * Get array of favorite menu options
   */
  get favoritesMenuOptions(): MenuOption[] {
    return this._menuOptions.filter((option) => option.security.isFavorite);
  }

  /**
   * Make active tab a favorite menu item
   * then update the favorites list
   * @param tab
   */
  private _addFavorite(menu: MenuOption) {
    const parm: MenuId = {
      menuId: menu.id
    };
    return this.http.addDataParams(parm).post(IPLUS_URL.MENU_ITEMS_TOGGLE_FAVORITE, "");
  }

  /**
   * Delete a favorite menu item
   * then update the favorites list
   * @param tab
   */
  private _deleteFavorite(menu: MenuOption) {
    const parm: MenuId = {
      menuId: menu.id
    };
    return this.http.addDataParams(parm).delete(IPLUS_URL.MENU_ITEMS_TOGGLE_FAVORITE);
  }

  /**
   * If favorites tab is open, refresh favorites array and emit changes
   * @param menu
   */
  private _updateFavoritesTab(menu: MenuOption): void {
    menu.security.isFavorite = !menu.security.isFavorite;
    this.messageService.sendFavoritesChangedMessage({ menuId: menu.id });
  }
}
